/**
 * Datos de las justificaciones en Staff (008 · T111; contracts/staff-api.md S2–S4, contracts/ui.md
 * §2.2–§2.3, research R-18).
 *
 * - `GET /attendance/justifications` (`useStaffJustifications`): las del trabajador de los últimos
 *   60 días, en el orden del servidor, con `still_applies` y `can_cancel` ya decididos. Aquí no se
 *   calcula ningún plazo, tope ni elegibilidad: lo que se puede justificar lo dice
 *   `StaffDay.justify.kinds` (`GET /attendance/days`).
 * - `POST /attendance/justifications` (`useCreateJustification`) y
 *   `POST /attendance/justifications/{id}/cancel` (`useCancelJustification`): sin optimistic UI; al
 *   terminar (o ante un `409` que dice que la situación cambió) se invalidan las vistas de
 *   asistencia (estado, historial, regularizaciones, días) **y** las justificaciones.
 * - La clave de idempotencia es la de la 007 (`newIdempotencyKey`): una por intento de envío.
 *
 * Rutas **autenticadas** del realm staff (firma `X-Fermin-Device`, cabecera anti-CSRF en las
 * mutaciones). Ningún motivo ni nota se loguea.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  JustificationUnavailableReasonSchema,
  StaffJustificationCancelResponseSchema,
  StaffJustificationMutationResponseSchema,
  StaffJustificationsResponseSchema,
  type CreateStaffJustificationBody,
  type JustificationKind,
  type StaffJustification,
  type StaffJustificationCancelResponse,
  type StaffJustificationMutationResponse,
  type StaffJustificationsResponse,
} from '@fermin/contracts';

import { api, ApiError } from '../../api/client';
import { t } from '../../i18n';
import { invalidateAttendanceViews } from '../history/api';

import { STAFF_JUSTIFICATIONS_QUERY_KEY } from './keys';

export { STAFF_JUSTIFICATIONS_QUERY_KEY };
export { newIdempotencyKey } from '../regularization/api';

export const JUSTIFICATIONS_PATH = '/attendance/justifications';

export function fetchStaffJustifications(
  signal?: AbortSignal,
): Promise<StaffJustificationsResponse> {
  return api.get(JUSTIFICATIONS_PATH, {
    schema: StaffJustificationsResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

export interface StaffJustificationsQueryOptions {
  enabled?: boolean;
}

/** Justificaciones del trabajador (≤ 100, recientes primero, en el orden del servidor). */
export function useStaffJustifications(
  options: StaffJustificationsQueryOptions = {},
): UseQueryResult<StaffJustificationsResponse, ApiError> {
  return useQuery<StaffJustificationsResponse, ApiError>({
    queryKey: STAFF_JUSTIFICATIONS_QUERY_KEY,
    queryFn: ({ signal }) => fetchStaffJustifications(signal),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}

/** Asistencia completa + justificaciones: lo que cambia al enviar o cancelar. */
export async function invalidateJustificationViews(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    invalidateAttendanceViews(queryClient),
    queryClient.invalidateQueries({ queryKey: STAFF_JUSTIFICATIONS_QUERY_KEY }),
  ]);
}

export function createJustification(
  body: CreateStaffJustificationBody,
): Promise<StaffJustificationMutationResponse> {
  return api.post(JUSTIFICATIONS_PATH, {
    body,
    schema: StaffJustificationMutationResponseSchema,
  });
}

/** Envía una justificación. Sin optimistic UI: la pantalla espera la respuesta. */
export function useCreateJustification(): UseMutationResult<
  StaffJustificationMutationResponse,
  ApiError,
  CreateStaffJustificationBody
> {
  const queryClient = useQueryClient();
  return useMutation<StaffJustificationMutationResponse, ApiError, CreateStaffJustificationBody>({
    mutationFn: createJustification,
    onSuccess: async () => {
      await invalidateJustificationViews(queryClient);
    },
    onError: async (error) => {
      if (isStaleJustificationError(error)) {
        await invalidateJustificationViews(queryClient);
      }
    },
  });
}

export function cancelJustification(id: string): Promise<StaffJustificationCancelResponse> {
  return api.post(`${JUSTIFICATIONS_PATH}/${encodeURIComponent(id)}/cancel`, {
    body: {},
    schema: StaffJustificationCancelResponseSchema,
  });
}

/** Cancela una justificación `PENDING` propia (cuenta para el tope por día y tipo). */
export function useCancelJustification(): UseMutationResult<
  StaffJustificationCancelResponse,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation<StaffJustificationCancelResponse, ApiError, string>({
    mutationFn: cancelJustification,
    onSuccess: async () => {
      await invalidateJustificationViews(queryClient);
    },
    onError: async (error) => {
      if (isStaleJustificationError(error)) {
        await invalidateJustificationViews(queryClient);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Selecciones puras (solo leen lo que decidió el servidor)
// ---------------------------------------------------------------------------

/** Tipos con una justificación `PENDING`, por día: "Justificación en revisión" bajo el día. */
export function pendingKindsByDay(
  items: readonly StaffJustification[] | undefined,
): ReadonlyMap<string, readonly JustificationKind[]> {
  const out = new Map<string, JustificationKind[]>();
  for (const item of items ?? []) {
    if (item.status !== 'PENDING') continue;
    const kinds = out.get(item.day) ?? [];
    if (!kinds.includes(item.kind)) kinds.push(item.kind);
    out.set(item.day, kinds);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------

/** El servidor dice que la situación ya cambió: hay que recargar, no reintentar. */
export function isStaleJustificationError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.is('JUSTIFICATION_UNAVAILABLE') ||
      error.is('JUSTIFICATION_NOT_OPEN') ||
      error.is('PERIOD_CLOSED') ||
      error.is('NOT_FOUND'))
  );
}

/** Texto es-PE de un error "la situación cambió", por `details.reason` (nunca literales). */
export function unavailableMessage(error: ApiError): string {
  if (error.is('JUSTIFICATION_NOT_OPEN') || error.is('NOT_FOUND')) {
    return t('justification.errors.JUSTIFICATION_NOT_OPEN');
  }
  if (error.is('PERIOD_CLOSED')) {
    return t('justification.errors.PERIOD_CLOSED');
  }
  const reason = JustificationUnavailableReasonSchema.safeParse(error.details?.reason);
  return reason.success
    ? t(`justification.unavailable.${reason.data}`)
    : t('justification.errors.JUSTIFICATION_UNAVAILABLE');
}

/** `400 VALIDATION_ERROR` → mensaje del campo `reason` (el único que escribe el trabajador). */
export function reasonErrorOf(error: ApiError): string | null {
  if (!error.is('VALIDATION_ERROR')) return null;
  const issues = error.details?.issues;
  if (!Array.isArray(issues)) return null;
  for (const issue of issues as unknown[]) {
    if (typeof issue !== 'object' || issue === null) continue;
    const { path, message } = issue as { path?: unknown; message?: unknown };
    if (
      typeof path === 'string' &&
      path.split('.').at(-1) === 'reason' &&
      typeof message === 'string'
    ) {
      return message;
    }
  }
  return null;
}
